import axios, { AxiosInstance } from 'axios';
import { Pedido, StatusPedido } from '../types/pedido';
import { ProjecoesMensaisResponse, ProjecoesAnuaisResponse } from '../types/projecoes';
import {
  DeliveryNeighborhoodReport,
  SalesReportResult,
  PaymentsReportResult,
  StockReportResult,
} from '../types/reports';
import authService from './authService';

type DynamoValue = {
  S?: string;
  N?: string;
  BOOL?: boolean;
  NULL?: boolean;
  M?: Record<string, DynamoValue>;
  L?: DynamoValue[];
};

const DYNAMO_KEYS = new Set(['S', 'N', 'BOOL', 'NULL', 'M', 'L']);

const isDynamoAttribute = (value: unknown): value is DynamoValue => {
  if (!value || typeof value !== 'object') return false;
  return Object.keys(value as Record<string, unknown>).some((key) => DYNAMO_KEYS.has(key));
};

const convertFromDynamo = (value: any): any => {
  if (Array.isArray(value)) {
    return value.map(convertFromDynamo);
  }

  if (isDynamoAttribute(value)) {
    if (value.S !== undefined) return value.S;
    if (value.N !== undefined) return Number(value.N);
    if (value.BOOL !== undefined) return value.BOOL;
    if (value.NULL) return null;
    if (value.M) {
      return Object.fromEntries(
        Object.entries(value.M).map(([key, val]) => [key, convertFromDynamo(val)])
      );
    }
    if (value.L) {
      return value.L.map(convertFromDynamo);
    }
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, val]) => [key, convertFromDynamo(val)])
    );
  }

  return value;
};

const normalizePedido = (raw: any): Pedido => {
  const data = convertFromDynamo(raw) as Record<string, any>;

  const enderecoDefault = {
    rua: '',
    numero: '',
    bairro: '',
    cidade: '',
    estado: '',
    cep: '',
    complemento: undefined,
  };

  const endereco = {
    ...enderecoDefault,
    ...(data.endereco ?? {}),
  };

  const itens = Array.isArray(data.itens)
    ? data.itens.map((item: any) => ({
        id: String(item.id ?? ''),
        nome: item.nome ?? '',
        quantidade: Number(item.quantidade ?? 0),
        preco: Number(item.preco ?? item.precoUnitario ?? 0),
        observacao: item.observacao,
        comboName: item.comboNome ?? item.comboName,
        comboId: item.comboId,
        comboKey: item.comboKey,
      }))
    : [];

  const valorBrutoItens = itens.reduce(
    (acc, item) => acc + item.preco * item.quantidade,
    0
  );

  const valorItens = Number(data.valorItens ?? valorBrutoItens ?? 0);
  const taxaEntrega = Number(data.taxaEntrega ?? 0);
  const couponCode = data.couponCode ?? data.cupomCodigo ?? undefined;
  const couponDiscount =
    data.couponDiscount !== undefined
      ? Number(data.couponDiscount)
      : data.valorDesconto !== undefined
      ? Number(data.valorDesconto)
      : undefined;

  const descontoCombos = Math.max(0, valorBrutoItens - valorItens);

  // Correção de compatibilidade: alguns registros antigos tiveram campos
  // salvos com ordem trocada (status em telefone, id em status, etc.).
  const validStatuses = Object.values(StatusPedido) as string[];

  // Se o status não é válido mas o telefone é um status válido, usamos o telefone como status
  let statusBruto: string | undefined = data.status;
  if (!validStatuses.includes(String(statusBruto)) && validStatuses.includes(String(data.telefone))) {
    statusBruto = data.telefone;
  }

  // Telefone não deve ser um dos statuses; se for, ignoramos
  const telefoneBruto = String(data.telefone ?? '');
  const telefone = validStatuses.includes(telefoneBruto) ? '' : telefoneBruto;

  return {
    id: data.id ?? data.idPedido ?? '',
    idPedido: data.id ?? data.idPedido ?? '',
    cliente: data.cliente ?? '',
    telefone,
    endereco,
    itens,
    valorItens,
    valorBrutoItens,
    descontoCombos,
    taxaEntrega,
    valorTotal: Number(
      data.valorTotal ?? valorItens + taxaEntrega - (couponDiscount ?? 0)
    ),
    status: (statusBruto && validStatuses.includes(String(statusBruto))
      ? (statusBruto as StatusPedido)
      : StatusPedido.RECEBIDO),
    dataCriacao: data.dataCriacao ?? new Date().toISOString(),
    dataAtualizacao: data.dataAtualizacao ?? data.dataCriacao ?? new Date().toISOString(),
    observacao: data.observacao,
    couponCode,
    couponDiscount,
    formaPagamento: data.formaPagamento,
    courierId: data.courierId ?? undefined,
    courierName: data.courierName ?? undefined,
    courierPhone: data.courierPhone ?? undefined,
    paymentStatus: data.paymentStatus ?? undefined,
    paymentMethod: data.paymentMethod ?? undefined,
    paymentId: data.paymentId ?? undefined,
  };
};

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar token de sessão (JWT) em todas as requisições
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor de resposta: em caso de 401 ou 403, efetua logout e redireciona para login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    if (status === 401 || status === 403) {
      console.warn('[API] Resposta de erro de autenticação, efetuando logout. Status:', status);
      authService.logout();
    }
    return Promise.reject(error);
  }
);

export const pedidoService = {
  async listar(filtros?: { dataInicio?: string; dataFim?: string; status?: StatusPedido }) {
    const response = await api.get('/pedidos', { params: filtros });
    const dados = response.data;
    if (Array.isArray(dados)) {
      return dados.map(normalizePedido);
    }
    return [];
  },

  async buscarPorId(id: string): Promise<Pedido> {
    const response = await api.get(`/pedidos/${id}`);
    return normalizePedido(response.data);
  },

  async atualizarStatus(id: string, status: StatusPedido, courierId?: string | null): Promise<Pedido> {
    const payload: { status: StatusPedido; courierId?: string | null } = { status };
    if (courierId !== undefined) {
      payload.courierId = courierId;
    }

    const response = await api.put(`/pedidos/${id}/status`, payload);
    return normalizePedido(response.data);
  },

  async criar(pedido: Omit<Pedido, 'id' | 'idPedido' | 'dataCriacao' | 'dataAtualizacao' | 'status'>): Promise<Pedido> {
    const response = await api.post('/pedidos', pedido);
    return normalizePedido(response.data);
  },

  async criarAvulso(payload: {
    cliente: string;
    telefone?: string;
    endereco: {
      rua: string;
      numero: string;
      bairro: string;
      cidade: string;
      estado: string;
      cep: string;
      complemento?: string;
    };
    itens: Array<{
      id: string;
      nome: string;
      quantidade: number;
      precoUnitario: number;
      observacao?: string;
      comboId?: string;
      comboNome?: string;
    }>;
    valorItens: number;
    taxaEntrega: number;
    observacao?: string;
    couponCode?: string;
    formaPagamento?: string;
    canal?: string;
  }): Promise<Pedido> {
    const response = await api.post('/pedidos', payload);
    return normalizePedido(response.data);
  },
};

export const projecoesService = {
  async getMensal(params?: { ano?: number; mes?: number }): Promise<ProjecoesMensaisResponse> {
    const response = await api.get('/admin/projecoes/mensal', { params });
    return response.data as ProjecoesMensaisResponse;
  },

  async getAnual(params?: { ano?: number }): Promise<ProjecoesAnuaisResponse> {
    const response = await api.get('/admin/projecoes/anual', { params });
    return response.data as ProjecoesAnuaisResponse;
  },
};

export const reportsService = {
  async getDeliveriesByNeighborhood(params: {
    dataInicio?: string;
    dataFim?: string;
    status?: string[];
    telefone?: string;
    cnpj?: string;
    courierId?: string;
  }): Promise<DeliveryNeighborhoodReport> {
    const response = await api.get('/admin/reports/deliveries/neighborhoods', {
      params: {
        ...params,
        status: params.status?.join(','),
      },
    });

    return response.data as DeliveryNeighborhoodReport;
  },

  async getSalesReport(params: {
    dataInicio?: string;
    dataFim?: string;
    status?: string[];
    canais?: string[];
    formasPagamento?: string[];
    cliente?: string;
    telefone?: string;
    cnpj?: string;
  }): Promise<SalesReportResult> {
    const response = await api.get('/admin/reports/sales', {
      params: {
        dataInicio: params.dataInicio,
        dataFim: params.dataFim,
        status: params.status?.length ? params.status.join(',') : undefined,
        canais: params.canais?.length ? params.canais.join(',') : undefined,
        formasPagamento: params.formasPagamento?.length ? params.formasPagamento.join(',') : undefined,
        cliente: params.cliente?.trim() || undefined,
        telefone: params.telefone?.trim() || undefined,
        cnpj: params.cnpj?.trim() || undefined,
      },
    });

    return response.data as SalesReportResult;
  },

  async getPaymentsReport(params: {
    startDate?: string;
    endDate?: string;
    status?: string[];
    paymentMethod?: string[];
    pedidoId?: string;
    providerPaymentId?: string;
    consumerId?: string;
    minAmount?: number;
    maxAmount?: number;
    limit?: number;
    lastEvaluatedKey?: Record<string, unknown>;
    cnpj?: string;
  }): Promise<PaymentsReportResult> {
    const response = await api.get('/admin/reports/payments', {
      params: {
        startDate: params.startDate,
        endDate: params.endDate,
        status: params.status?.length ? params.status.join(',') : undefined,
        paymentMethod: params.paymentMethod?.length ? params.paymentMethod.join(',') : undefined,
        pedidoId: params.pedidoId?.trim() || undefined,
        providerPaymentId: params.providerPaymentId?.trim() || undefined,
        consumerId: params.consumerId?.trim() || undefined,
        minAmount: params.minAmount,
        maxAmount: params.maxAmount,
        limit: params.limit,
        lastEvaluatedKey: params.lastEvaluatedKey ? JSON.stringify(params.lastEvaluatedKey) : undefined,
        cnpj: params.cnpj?.trim() || undefined,
      },
    });

    return response.data as PaymentsReportResult;
  },

  async getStockReport(params: {
    startDate?: string;
    endDate?: string;
    searchTerm?: string;
    category?: string;
    includeInactive?: boolean;
    includeZeroBalances?: boolean;
    movementPageSize?: number;
    maxMovementPages?: number;
    cnpj?: string;
  }): Promise<StockReportResult> {
    const response = await api.get('/admin/reports/stock', {
      params: {
        startDate: params.startDate,
        endDate: params.endDate,
        searchTerm: params.searchTerm?.trim() || undefined,
        category: params.category?.trim() || undefined,
        includeInactive: params.includeInactive,
        includeZeroBalances: params.includeZeroBalances,
        movementPageSize: params.movementPageSize,
        maxMovementPages: params.maxMovementPages,
        cnpj: params.cnpj?.trim() || undefined,
      },
    });

    return response.data as StockReportResult;
  },
};

export default api;

export const getApiEnvConfig = () => ({
  apiKey: '',
  companyCnpj: '',
});
